import { Payload, config } from "./config";
type TableRow = { [key: string]: any };

export class Utils {
    public static hide(id: string) {
        this.getElement(id).classList.add("hidden");
    }

    public static show(id: string) {
        this.getElement(id).classList.remove("hidden");
    }

    /**
     * Enable/disable the client-scoped menu items deterministically.
     * Driven by the current view (client selected vs. list), so it can't
     * desync the way a blind toggle does.
     */
    public static setClientMenu(enabled: boolean) {
        document.querySelectorAll('.client-menu').forEach(element => {
            element.classList.toggle('disabled', !enabled);
        });
    }

    public static idToggle(id: string, name: string) {
        this.getElement(id).classList.toggle(name);
    }

    public static showhide(id: string, show: boolean) {
        if (show)
            this.show(id);
        else
            this.hide(id);
    }

    public static getElement(id: string, clear: boolean = false): HTMLElement {
        const element = document.getElementById(id);
        if (!element)
            throw new Error("missing essential element: " + id);
        if (clear)
            element.innerHTML = "";
        return element;
    }

    public static populate(id: string, data: Payload) {
        // Fill-only: visibility is driven by the Alpine `ui` store, not here.
        const element = this.getElement(id);
        if (!element)
            return;

        // Find all input elements within the container
        const inputs = element.querySelectorAll('input');

        inputs.forEach(input => {
            const id = input.id;
            // Check if the data object has a matching property
            if (id && id in data)
                input.value = String(data[id]);
            else
                input.value = "";
        });
    }

    public static getValues(id: string): Payload {
        const form = this.getElement(id);
        if (!form)
            throw new Error("Element not found: " + id);

        if (!form.classList.contains('hidden')) {
            form.classList.add('hidden');
        }

        // Find all input elements within the container
        const elements = form.querySelectorAll('input, select');

        const data: Payload = {};
        elements.forEach(element => {
            const formElement = element as HTMLInputElement | HTMLSelectElement;
            if (formElement.disabled)
                return;

            if (formElement.id) {
                let value: any;
                // Convert to number if input type is 'number'
                if (formElement instanceof HTMLInputElement && formElement.type === 'number') {
                    value = formElement.value ? Number(formElement.value) : '';
                } else {
                    if (formElement.value === "true")
                        value = true;
                    else if(formElement.value === "false")
                        value = false;
                    else
                        value = formElement.value;
                }
                // console.log(formElement.id, config.map[id]?[formElement.id], value);
                const mappedKey = config.map[id]?.[formElement.id];
                if (mappedKey) {
                    if (mappedKey == "supersede") {  // special case - only include if it's visible
                        const div = this.getElement(id + "Supersede");
                        // console.log(div);
                        if (div.classList.contains('hidden'))
                            return;
                    }
                    if (value !== null && value !== undefined && value !== '') {
                        if (!data[mappedKey]) {
                            data[mappedKey] = {}; // Initialize nested object if it doesn't exist
                        }
                        data[mappedKey][formElement.id] = value; // Assign value to the nested object
                    }
                }
                else
                    data[formElement.id] = value; // Assign value to the top-level object
            }
        });

        // custom checks
        switch (id) {
            case 'requestBonusPayment':
                if (!data['lifeEventId'])
                    delete data['lifeEventId'];
                break;
        }

        return data;
    }

    public static validate(id: string): boolean {
        console.log("validate form: " + id);
        const form = this.getElement(id);
        if (!form)
            throw new Error("Element not found: " + id);

        const inputs = form.querySelectorAll('input');
        const selects = form.querySelectorAll('select');
        let isValid = true;
        let errorMessages: string[] = [];
        const data = [];

        inputs.forEach((input) => {
            if (input.disabled)
                return;

            data[input.name] = input.value;            // Validate required
            if (input.required && !input.value) {
                errorMessages.push(`The field "${input.name}" is required.`);
                isValid = false;
                return; // skip pattern check
            }

            // Validate pattern
            const pattern = input.getAttribute('pattern');
            if (pattern) {
                const regex = new RegExp(pattern);
                if (!regex.test(input.value)) {
                    errorMessages.push(`The field "${input.name}" does not match the required pattern.`);
                    isValid = false;
                }
            }
        });

        selects.forEach((select) => {
            data[select.name] = select.value;
        });


        if (!isValid) {
            console.log("finished validation failed");
            // alert(`Please fix the following errors:\n\n${errorMessages.join('\n')}`);
            this.showError("Please fix the following errors:", errorMessages);  // needs dedicated error box
        }
        else {
            if (!this.actionValidate(id, data))
                isValid = false;
        }
        return isValid;
    }

    /**
     * Custom validation according to event id
     * @param id
     * @param data
     */
    private static actionValidate(id, data): boolean {
        // console.log("actionValidate: " + id, data);
        let error = "";
        switch (id) {
            case 'requestBonusPayment':
                if (data['claimReason'] == "Life Event" && ! /^\d{10}$/.test(data['lifeEventId']))
                    error = "Life Event Id must be supplies when claim reason if Life Event";
                break;
        }
        if (error) {
            this.showError("Please fix the following errors:", {error: error});
            return false;
        }
        return true;
    }

    public static showInfo(id: string, data: Payload) {
        const dialog = (window as any).Alpine?.store('dialog');
        if (dialog)
            dialog.show(this.camelToSentence(id), this.toRows(data));
    }

    public static showError(id: string, data: Payload) {
        const dialog = (window as any).Alpine?.store('dialog');
        if (dialog)
            dialog.showError(this.camelToSentence(id), this.toRows(data));
    }


    public static showTable(id: string, data: TableRow[]) {
        const dialog = (window as any).Alpine?.store('dialog');
        if (!dialog)
            return;
        const keys = data.length ? Object.keys(data[0]) : [];
        const headers = keys.map(k => ({ key: k, label: this.camelToSentence(k) }));
        dialog.showTable(this.camelToSentence(id), headers, data);
    }

    /**
     * Flatten a value into label/value rows for the dialog. Returns plain strings
     * (no HTML) — the dialog renders them with x-text, so values are auto-escaped.
     */
    private static toRows(data: any): { label: string; value: string }[] {
        const rows: { label: string; value: string }[] = [];
        const valToString = (v: any): string => {
            if (v === null || v === undefined)
                return "";
            if (typeof v === 'object')
                return Object.keys(v).map(k => `${k}: ${v[k]}`).join("\n");
            return String(v);
        };
        const pushObject = (obj: any) => {
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key))
                    rows.push({ label: this.camelToSentence(key), value: valToString(obj[key]) });
            }
        };

        if (Array.isArray(data)) {
            data.forEach((item, index) => {
                if (item && typeof item === 'object')
                    pushObject(item);
                else
                    rows.push({ label: String(index), value: valToString(item) });
            });
        } else if (data && typeof data === 'object') {
            pushObject(data);
        } else {
            rows.push({ label: "", value: valToString(data) });
        }
        return rows;
    }

    public static setDisabled(id: string, state: boolean) {
        const element = document.getElementById(id) as HTMLInputElement;
        if (element)
            element.disabled = state;
    }

    public static showLoader() {
        this.getElement(config.loader).classList.add("active");
    }

    public static hideLoader() {
        this.getElement(config.loader).classList.remove("active");
    }

    public static camelToSentence(text: string): string {
        return text
            .replace(/([A-Z])/g, ' $1')
            .replace(/([A-Z])([A-Z])(?=[a-z])/g, '$1 $2')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }
}


// Create a custom namespace under `window` and assign `Utils`
(window as any).lisaManagerApp = {
    Utils
};